import { IVesselActivity, VesselActivityType } from "./model/vessel-activity";
import { IVesselStop, VesselStopTypeForStepper } from "../vessel-stop";
import { VesselTransitStatus } from "./model";
import { initialVesselMovementState, IVesselMovement, VesselActivityStatus, VesselMovementType } from "../vessel-movement";
import { CargoHelper, ICargo, ILocationForNOA } from "@pmis3/master-data-core";
import { Guid } from "@pmis3/core";
import { signal, WritableSignal } from "@angular/core";
import { VesselActivityIndicesAndStopType } from 'libs/vessel-activity/vessel-activity-core/src/lib/vessel-visit/model/vessel-activity';

type IVesselMovementRequiredFields = {
    key: keyof IVesselMovement;
    minValue?: any;
    maxValue?: any;
}

type IVesselStopRequiredFields = {
    key: keyof IVesselStop;
    minValue?: any;
    maxValue?: any;
}

export class VesselActivityHelper {
    static getStopType(vesselActivities: IVesselActivity[], vesselActivityIndex: number): VesselStopTypeForStepper | null {
        const currentActivity = vesselActivities[vesselActivityIndex];

        // logic for filtering for only active stops
        // const isFirstActiveStop = this.isVesselStop(currentActivity) &&
        //     vesselActivities
        //         .slice(0, vesselActivityIndex)
        //         .filter(va => this.isVesselStop(va))
        //         .every(va => va.status === VesselTransitStatus.Cancelled);
        
        const isFirstActiveStop = false;

        if (isFirstActiveStop) {
            return VesselStopTypeForStepper.Arrival;
        } else if (vesselActivityIndex === vesselActivities.length - 1 && currentActivity.activityType == VesselActivityType.Movement) {
            return VesselStopTypeForStepper.Departure;
        } else if (currentActivity.activityType == VesselActivityType.Stop) {
            return VesselStopTypeForStepper.Shift;
        } else {
            return null;
        }
    }

    static isVesselStop(vesselActivity: IVesselActivity) {
        return vesselActivity.activityType == VesselActivityType.Stop;
    }

    static getStopNumber(vesselActivities: IVesselActivity[], vesselStopIndex: number): number | undefined {
        if (!vesselActivities || vesselStopIndex < 0 || vesselStopIndex >= vesselActivities.length) {
            return undefined;
        }

        let currentStopNumber = 0;
        for (let i = 0; i <= vesselStopIndex; i++) {
            if (this.isVesselStop(vesselActivities[i])) {
                currentStopNumber++;
            }
        }

        // departure is not a stop; its always the last movement
        return this.getStopType(vesselActivities, vesselStopIndex) == VesselStopTypeForStepper.Departure ? currentStopNumber + 1 : currentStopNumber;
    }

    // static getVesselActivityStatus(vesselActivities: IVesselActivity[], vesselActivity: IVesselActivity): string | undefined {
    //     if (this.isVesselStop(vesselActivity)) {
    //         return (vesselActivity as IVesselStop).status;
    //     }

    //     const index = vesselActivities.indexOf(vesselActivity);
    //     if (index === -1) {
    //         return undefined;
    //     }

    //     let prevStatus: string | undefined = undefined;
    //     for (let i = index - 1; i >= 0; i--) {
    //         if (this.isVesselStop(vesselActivities[i])) {
    //             prevStatus = (vesselActivities[i] as IVesselStop).status;
    //             break;
    //         }
    //     }

    //     let nextStatus: string | undefined = undefined;
    //     for (let i = index + 1; i < vesselActivities.length; i++) {
    //         if (this.isVesselStop(vesselActivities[i])) {
    //             nextStatus = (vesselActivities[i] as IVesselStop).status;
    //             break;
    //         }
    //     }

    //     if (prevStatus === VesselTransitStatus.Completed && nextStatus === VesselTransitStatus.Completed) {
    //         return VesselTransitStatus.Completed;
    //     } else {
    //         return VesselTransitStatus.Approved;
    //     }
    // }

    static createVesselMovementAndStop(vesselVisitId: number, startTime?: Date, estimatedEndTime?: Date, locationId?: number, location?: ILocationForNOA, cloningMovement?: IVesselMovement): IVesselActivity[] {
        let vesselMovement;
        if (cloningMovement) {
            const newCargoes = cloningMovement?.cargoes?.map(cargo => {
                return {
                    ...cargo,
                    id: 0,
                    isAdded: true,
                    stopId: 0
                } as ICargo;
            })
            vesselMovement = {
                ...cloningMovement,
                id: 0,
                endLocationId: locationId,
                endLocation: location,
                estimatedEndTime: startTime,
                isAdded: true,
                cargoes: newCargoes,
                statusId: VesselActivityStatus.Pending,
                ppaStatus: VesselActivityStatus.Pending,
                vfpaStatus: VesselActivityStatus.Pending,
            } as IVesselMovement;
        } else {
            const arrivalMovementStartLocation = undefined;
            vesselMovement = this.createNewVesselMovement(vesselVisitId, startTime, arrivalMovementStartLocation, arrivalMovementStartLocation, locationId, location);
        }

        const vesselStop: IVesselStop = this.createNewVesselStop(vesselVisitId, estimatedEndTime, locationId, location);
        return [vesselMovement, vesselStop]
    }

    static createInitialStop(vesselVisitId: number, startTime: Date, estimatedEndTime?: Date, locationId?: number, location?: ILocationForNOA): IVesselActivity[] {
        const arrivalMovementStartLocation = undefined;
        const departureMovementEndTime = undefined

        const arrivalMovement = this.createNewVesselMovement(vesselVisitId, startTime, arrivalMovementStartLocation, arrivalMovementStartLocation, locationId, location);
        const vesselStop = this.createNewVesselStop(vesselVisitId, estimatedEndTime, locationId, location);
        const departureMovement = this.createNewVesselMovement(vesselVisitId, departureMovementEndTime, locationId, location);

        return [arrivalMovement, vesselStop, departureMovement];
    }

    static updateMovementAfterCloning(movement: IVesselMovement): IVesselMovement {
        const deletedCargoes = movement.cargoes?.map(cargo => {
            return {
                ...cargo,
                isDeleted: true
            }
        })
        return {
            id: movement.id,
            endLocationId: movement.endLocationId,
            endLocation: movement.endLocation,
            estimatedEndTime: movement.estimatedEndTime,
            endTime: movement.endTime,
            vesselVisitId: movement.vesselVisitId,
            activityType: VesselActivityType.Movement,
            statusId: VesselActivityStatus.Pending,
            ppaStatus: VesselActivityStatus.Pending,
            vfpaStatus: VesselActivityStatus.Pending,
            cargoes: deletedCargoes
        }
    }

    // static createDepartureMovement(endTime?: Date, startLocationId?: string, startLocation?: ILocation, endLocationId?: string, endLocation?: ILocation): IVesselMovement {
    //     return this.createNewVesselMovement(endTime, startLocationId, startLocation, endLocationId, endLocation)
    // }

    static createNewVesselMovement(vesselVisitId: number, estimatedEndTime?: Date, startLocationId?: number, startLocation?: ILocationForNOA, endLocationId?: number, endLocation?: ILocationForNOA): IVesselMovement {
        return {
            // id: Guid.newGuid().toString().replace(/\D/g, '').slice(0, 3),
            id: 0,
            estimatedEndTime: estimatedEndTime,
            startLocationId: startLocationId,
            startLocation: startLocation,
            endLocationId: endLocationId,
            endLocation: endLocation,
            vesselVisitId: vesselVisitId,
            isAdded: true,
            activityType: VesselActivityType.Movement,
            statusId: VesselActivityStatus.Pending,
            vfpaStatus: VesselActivityStatus.Pending,
            ppaStatus: VesselActivityStatus.Pending
        };
    }

    static createNewVesselStop(vesselVisitId: number, estimatedEndTime?: Date, startLocationId?: number, startLocation?: ILocationForNOA): IVesselStop {
        return {
            id: 0,
            estimatedEndTime: estimatedEndTime,
            startLocationId: startLocationId,
            startLocation: startLocation,
            // status: VesselTransitStatus.New,
            vesselVisitId: vesselVisitId,
            isAdded: true,
            activityType: VesselActivityType.Stop
        };
    }

    static insertVesselActivitiesAt(vesselActivities: IVesselActivity[], index: number, vesselActivitiesToInsert: IVesselActivity[]) {
        return [
            ...vesselActivities.slice(0, index),
            ...vesselActivitiesToInsert,
            ...vesselActivities.slice(index)
        ] as IVesselActivity[]
    }

    static updateVesselActivities(
        originalVesselActivities: IVesselActivity[],
        vesselVisitId: number,
        insertPosition: number,
        startTime: Date,
        endTime: Date | undefined,
        selectedBerth: ILocationForNOA | undefined
    ): IVesselActivity[] {
        let updatedVesselActivities = originalVesselActivities;
        let newVesselActivities: IVesselActivity[];

        const activities = originalVesselActivities
        const isActive = (activity?: IVesselActivity) => !!activity && !this.activityIsDeleted(activity);

        const findPrevMovement = (): IVesselMovement | undefined => {
            for (let i = Math.min(insertPosition, activities.length - 1); i >= 0; i--) {
                const a = activities[i];
                if (isActive(a) && a.activityType === VesselActivityType.Movement) {
                    return a as IVesselMovement;
                }
            }
            return undefined;
        };

        const findPrevStop = (): IVesselStop | undefined => {
            for (let i = Math.min(insertPosition, activities.length - 1); i >= 0; i--) {
                const a = activities[i];
                if (isActive(a) && a.activityType === VesselActivityType.Stop) {
                    return a as IVesselStop;
                }
            }
            return undefined;
        };

        if (activities.length === 0) {
            newVesselActivities = this.createInitialStop(
                vesselVisitId,
                startTime,
                endTime,
                selectedBerth?.id,
                selectedBerth
            );
        } else {
            const cloningMovement = findPrevMovement();

            newVesselActivities = this.createVesselMovementAndStop(
                vesselVisitId,
                startTime,
                endTime,
                selectedBerth?.id,
                selectedBerth,
                cloningMovement
            );

            if (cloningMovement && !this.activityIsDeleted(cloningMovement)) {
                const updatedCloningMovement = this.updateMovementAfterCloning(cloningMovement);
                updatedVesselActivities = this.updateVesselActivityInArray(
                    updatedCloningMovement,
                    updatedVesselActivities,
                    activities.indexOf(cloningMovement)
                );
            }

            const prevStop = findPrevStop();
            if (prevStop){
                newVesselActivities = newVesselActivities.map(va => 
                    va.activityType === VesselActivityType.Movement ? ({
                        ...va,
                        estimatedStartTime: va.estimatedStartTime ?? prevStop.estimatedEndTime ?? null,
                        startTime: va.startTime ?? prevStop.endTime ?? null
                    } as IVesselMovement) : va
                )
            }
        }

        return this.insertVesselActivitiesAt(
            updatedVesselActivities,
            insertPosition,
            newVesselActivities
        );
    }

    static activityIsDeleted(activity: IVesselActivity): boolean {
        return activity.isDeleted === true;
    }

    static getPreviousVesselActivity(vesselActivities: IVesselActivity[], currentIndex: number) {
        for (let i = currentIndex - 1; i >= 0; i--) {
            const a = vesselActivities[i];
            if (!this.activityIsDeleted(a)) return a;
        }
        return null;
    }

    static getNextVesselActivity(vesselActivities: IVesselActivity[], currentIndex: number) {
        for (let i = currentIndex + 1; i < vesselActivities.length; i++) {
            const a = vesselActivities[i];
            if (!this.activityIsDeleted(a)) return a;
        }
        return null;
    }

    static sortVesselActivities(vesselActivities: IVesselActivity[]) {
        return [...vesselActivities].sort((a, b) => {
            const aTime = a.estimatedEndTime;
            const bTime = b.estimatedEndTime;

            if (!aTime && !bTime) return 0;
            if (!aTime) return 1;
            if (!bTime) return -1;

            return aTime.getTime() - bTime.getTime();
        })
    }

    static getRequiredFieldsForStop(): IVesselStopRequiredFields[] {
        return [
            { key: 'startLocationId' },
            { key: 'estimatedEndTime' }
        ];
    }

    static getRequiredFieldsForMovement(): IVesselMovementRequiredFields[] {
        return [
            // { key: 'startLocationId' },
            { key: 'loadedStatus' },
            { key: 'airDraft', minValue: 1, maxValue: 90 },
            { key: 'aftDraft', minValue: 1, maxValue: 19 },
            { key: 'midDraft', minValue: 1, maxValue: 19 },
            { key: 'foreDraft', minValue: 1, maxValue: 19 },
            // { key: 'estimatedEndTime' }
        ];
    }

    static activityFieldIsValid<
        T extends IVesselActivity,
        K extends keyof T
    >(
        field: K,
        activity: T,
        getRequiredFields: () => { key: keyof T; minValue?: any; maxValue?: any }[]
    ): boolean {
        const fieldConstraint = getRequiredFields().find(req => req.key === field);
        if (!fieldConstraint) return true;

        const value = activity[field];
        if (value == null) return false;

        const { minValue, maxValue } = fieldConstraint;

        if (minValue != null && value < minValue) return false;
        if (maxValue != null && value > maxValue) return false;

        return true;
    }

    static movementFieldIsValid(
        field: keyof IVesselMovement,
        movement: IVesselMovement, 
        previousActivity?: IVesselActivity | null, 
        nextActivity?: IVesselActivity | null, 
        // pilotStationETD?: Date
    ): boolean {
        if (field == 'estimatedEndTime' || field == 'endTime') {
            const useActual = field === 'endTime';
            return this.activityFieldIsValid(field, movement, this.getRequiredFieldsForMovement) && 
            this.activityEndTimeIsValid(
                movement, 
                previousActivity, 
                nextActivity,
                useActual 
                // pilotStationETD
            )
        }
        return this.activityFieldIsValid(field, movement, this.getRequiredFieldsForMovement);
    }

    static stopFieldIsValid(field: keyof IVesselStop, stop: IVesselStop, previousActivity?: IVesselActivity | null, nextActivity?: IVesselActivity | null): boolean {
        if (field == 'estimatedEndTime') {
            return this.activityFieldIsValid(field, stop, this.getRequiredFieldsForStop) && this.activityEndTimeIsValid(stop, previousActivity, nextActivity)
        }
        return this.activityFieldIsValid(field, stop, this.getRequiredFieldsForStop);
    }

    static activityEndTimeIsValid(
        vesselActivity: IVesselActivity,
        previousActivity?: IVesselActivity | null,
        nextActivity?: IVesselActivity | null,
        useActualEndTime: boolean = false,
        // pilotStationETD?: Date,
    ): boolean {
        const endTime = useActualEndTime ? vesselActivity.endTime : vesselActivity.estimatedEndTime;
        if (!endTime) return useActualEndTime ? true : false;
        if (!previousActivity) return true;

        // if (!previousActivity && pilotStationETD) {
        //     return endTime > pilotStationETD
        // }

        const previousEndTime = useActualEndTime ? previousActivity?.endTime : previousActivity?.estimatedEndTime;
        const nextEndTime = useActualEndTime ? nextActivity?.endTime : nextActivity?.estimatedEndTime;

        if (previousEndTime && endTime < previousEndTime) {
            return false;
        }

        if (nextEndTime && endTime > nextEndTime) {
            return false;
        }

        return true;
    }

    static movementIsValid(
        movement: IVesselMovement, 
        movementIndex: number, 
        vesselActivities: IVesselActivity[], 
        movementType: VesselMovementType
        // pilotStationETD?: Date
    ) {
        if(this.activityIsDeleted(movement)) return true;
        
        const previousActivity = this.getPreviousVesselActivity(vesselActivities, movementIndex);
        const nextActivity = this.getNextVesselActivity(vesselActivities, movementIndex);
        const requiredFields = this.getRequiredFieldsForMovement();

        if(movementType === VesselMovementType.Arrival) {
            requiredFields.push({ key: 'estimatedEndTime' });
        }

        return requiredFields
        .every(fieldConstraint => 
            this.movementFieldIsValid(
                fieldConstraint.key, 
                movement, 
                previousActivity, 
                nextActivity, 
                // pilotStationETD
            ));
    }

    static stopIsValid(stop: IVesselStop, stopIndex: number, vesselActivities: IVesselActivity[]) {
        const previousActivity = this.getPreviousVesselActivity(vesselActivities, stopIndex);
        const nextActivity = this.getNextVesselActivity(vesselActivities, stopIndex);
        return this.getRequiredFieldsForStop().every(fieldConstraint => this.stopFieldIsValid(fieldConstraint.key, stop, previousActivity, nextActivity));
    }
    
    static getIndicesForEditingOnSelectingStop(stopIndex: number, vesselActivities: IVesselActivity[]): VesselActivityIndicesAndStopType[] {
        const arrivalIndex = stopIndex - 1;
        const arrivalMovement = arrivalIndex < vesselActivities.length && arrivalIndex >= 0 && vesselActivities[arrivalIndex].activityType == VesselActivityType.Movement
            ? vesselActivities[arrivalIndex]
            : null;

        const departureIndex = stopIndex + 1;
        const departureMovement = departureIndex < vesselActivities.length && departureIndex >= 0 && vesselActivities[departureIndex].activityType == VesselActivityType.Movement
            ? vesselActivities[departureIndex]
            : null;

        const stop = stopIndex < vesselActivities.length && stopIndex >= 0 && vesselActivities[stopIndex].activityType == VesselActivityType.Stop
            ? vesselActivities[stopIndex]
            : null;

        const indices = arrivalMovement && departureMovement && stop ? [arrivalIndex, stopIndex, departureIndex] : []

        return this.getTypedIndicesForEditingVesselActivities(indices);
    }

    static updateVesselActivityInArray(newVesselActivity: IVesselActivity, vesselActivities: IVesselActivity[], index: number) {
        if (index < 0 || index >= vesselActivities.length) return vesselActivities;

        const updated = [...vesselActivities];
        updated[index] = newVesselActivity;
        return updated;
    }

    static getIndexOfArrivalMovementToStop(stopIndex: number, vesselActivities: IVesselActivity[]) {
        const movementIndex = stopIndex > 0 ? stopIndex - 1 : null;
        if (movementIndex == null) return null;

        const isMovement = !this.isVesselStop(vesselActivities[movementIndex])
        if (isMovement) return movementIndex;

        return null;
    }

    static getArrivalMovementToStop(stopIndex: number, vesselActivities: IVesselActivity[]) {
        const movementIndex = this.getIndexOfArrivalMovementToStop(stopIndex, vesselActivities);
        if (movementIndex == null) {
            return null;
        }
        return vesselActivities[movementIndex];
    }

    static getStatusBgColour(name?: string): string {
        switch (name) {
            case VesselTransitStatus.Approved:
                return 'bg-green';
            case VesselTransitStatus.Cancelled:
                return 'bg-black';
            // case VesselTransitStatus.Completed:
            //     return 'bg-grey';
            case VesselTransitStatus.Pending:
                return 'bg-orange';
            case VesselTransitStatus.Draft:
                return 'bg-blue';
            default:
                return 'bg-grey';
        }
    }
    static getStatusColour(name?: string): string {
        switch (name) {
            case VesselTransitStatus.Approved:
                return 'green';
            case VesselTransitStatus.Cancelled:
                return 'black';
            // case VesselTransitStatus.Completed:
            //     return 'bg-grey';
            case VesselTransitStatus.Pending:
                return 'orange';
            case VesselTransitStatus.Draft:
                return 'blue';
            default:
                return 'grey';
        }
    }

    static getTypedIndicesForEditingVesselActivities(indices: number[]) {
        const mappedIndicesAndType: VesselActivityIndicesAndStopType[] = [];
        if (!indices) return mappedIndicesAndType;

        for (let i = 0; i < indices?.length; i++) {
            const index = indices[i];
            let type: VesselStopTypeForStepper | string;
            if (i == 0) {
                type = VesselStopTypeForStepper.Arrival
            } else if (i == 1) {
                type = VesselStopTypeForStepper.Shift
            } else {
                type = VesselStopTypeForStepper.Departure
            }

            mappedIndicesAndType.push({
                index: index,
                type: type
            } as VesselActivityIndicesAndStopType)
        }

        return mappedIndicesAndType;

        // const stop = mappedIndicesAndType.find(item => item.type === "LOCATION");
        // const arrivalAndDeparture = mappedIndicesAndType.filter(item => item.type !== "LOCATION");
        // return stop ? [stop, ...arrivalAndDeparture] : arrivalAndDeparture;
    }

    static getTimeForActivity(vesselActivity?: IVesselActivity) {
        return vesselActivity?.endTime ?? vesselActivity?.estimatedEndTime;
    }

    static updateVesselActivitiesOnLocationChange(vesselActivities: IVesselActivity[], location?: ILocationForNOA, arrivalIndex?: number, stopIndex?: number, departureIndex?: number): IVesselActivity[] {
        if (stopIndex) {
            const updatedStop = {
                ...vesselActivities[stopIndex],
                startLocation: location,
                startLocationId: location?.id
            } as IVesselStop;
            vesselActivities = this.updateVesselActivityInArray(updatedStop, vesselActivities, stopIndex);
        }

        if (arrivalIndex != undefined) {
            const updatedArrivalMovement = {
                ...vesselActivities[arrivalIndex],
                endLocation: location,
                endLocationId: location?.id
            } as IVesselMovement;
            vesselActivities = this.updateVesselActivityInArray(updatedArrivalMovement, vesselActivities, arrivalIndex);
        }

        if (departureIndex) {
            const updatedDepartureMovement = {
                ...vesselActivities[departureIndex],
                startLocation: location,
                startLocationId: location?.id
            } as IVesselMovement;
            vesselActivities = this.updateVesselActivityInArray(updatedDepartureMovement, vesselActivities, departureIndex);
        }
        return vesselActivities;
    }
}